import { FieldType, IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  DeliveryTypeEnum,
  SmopOrderStatusEnum,
  StaffNameInput,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import {
  createAutocompleteField,
  createDateField,
  createMultipleTextField,
  createOptionsField,
  createTextField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum StoreOrderFilterKeys {
  created = "created",
  customer = "customer",
  deliveryType = "deliveryType",
  status = "status",
  shipping = "shipping",
  verticalType = "verticalType",
  store = "store",
  paymentType = "paymentType",
  runnerName = "runnerName",
  runnerFirstName = "runnerFirstName",
  runnerLastName = "runnerLastName",
  mall = "mall"
}

export enum PaymentType {
  CASH = "mirumee.payments.cash_on_delivery",
  CARD = "smop.payments.xendit",
  EWALLET_GRABPAY = "smop.payments.ewallet:GRABPAY",
  EWALLET_PAYMAYA = "smop.payments.ewallet:PAYMAYA",
  GCASH = "smop.payments.gcash",
  ONLINE_BANKING_BPI = "smop.payments.direct_debit:BPI",
  ONLINE_BANKING_CHINABANK = "smop.payments.direct_debit:CHINABANK",
  ONLINE_BANKING_RCBC = "smop.payments.direct_debit:RCBC",
  ONLINE_BANKING_UBP = "smop.payments.direct_debit:UBP"
}

export interface StoreOrderListFilterOpts {
  created: FilterOpts<MinMax>;
  customer: FilterOpts<string>;
  status: FilterOpts<SmopOrderStatusEnum>;
  shipping: FilterOpts<MinMax>;
  deliveryType: FilterOpts<DeliveryTypeEnum[]>;
  verticalType: FilterOpts<StoreTypeEnum[]>;
  store: FilterOpts<string>;
  paymentType: FilterOpts<PaymentType>;
  runnerName: FilterOpts<StaffNameInput>;
  mall?: FilterOpts<string[]> & AutocompleteFilterOpts;
}

const messages = defineMessages({
  card: {
    defaultMessage: "Card",
    description: "payment type"
  },
  cash: {
    defaultMessage: "Cash",
    description: "payment type"
  },
  customer: {
    defaultMessage: "Customer",
    description: "order"
  },
  deliveryType: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  },
  eWalletGrabPay: {
    defaultMessage: "E-Wallet - GrabPay",
    description: "payment type"
  },
  eWalletMaya: {
    defaultMessage: "E-Wallet - Maya",
    description: "payment type"
  },
  express: {
    defaultMessage: "Express",
    description: "express"
  },
  expressLongDistance: {
    defaultMessage: "10km Express Delivery",
    description: "expressLongDistance"
  },
  foodAndBeverages: {
    defaultMessage: "Food and Beverage",
    description: "food and beverage"
  },
  gcash: {
    defaultMessage: "GCash",
    description: "payment type"
  },
  generalMerchandise: {
    defaultMessage: "General Merchandise",
    description: "general merchandise"
  },
  inStore: {
    defaultMessage: "In Store Pickup",
    description: "in store pickup"
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall"
  },
  nationwide: {
    defaultMessage: "Nationwide Delivery",
    description: "nationwide delivery"
  },
  nextDay: {
    defaultMessage: "Standard Delivery",
    description: "standard delivery"
  },
  onlineBankingBPI: {
    defaultMessage: "Online Banking - BPI",
    description: "payment type"
  },
  onlineBankingChinaBank: {
    defaultMessage: "Online Banking - Chinabank",
    description: "payment type"
  },
  onlineBankingRcbc: {
    defaultMessage: "Online Banking - RCBC",
    description: "payment type"
  },
  onlineBankingUbp: {
    defaultMessage: "Online Banking - UBP",
    description: "payment type"
  },
  paymentType: {
    defaultMessage: "Payment Type",
    description: "payment type"
  },
  placed: {
    defaultMessage: "Order Date",
    description: "order"
  },
  sameDay: {
    defaultMessage: "Same Day Delivery",
    description: "same day delivery"
  },
  shipping: {
    defaultMessage: "Delivery Date",
    description: "order"
  },
  store: {
    defaultMessage: "Store",
    description: "store"
  },
  verticalType: {
    defaultMessage: "Vertical Type",
    description: "vertical type"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: StoreOrderListFilterOpts,
  toExcludeOptions?: StoreOrderFilterKeys[]
): IFilter<StoreOrderFilterKeys> {
  const filterStructure = [
    {
      ...createMultipleTextField(StoreOrderFilterKeys.runnerName, "Runner", [
        {
          active: opts.runnerName.active,
          label: "First name",
          multiple: false,
          name: StoreOrderFilterKeys.runnerFirstName,
          type: FieldType.text,
          value: opts.runnerName.value.firstName
        },
        {
          active: opts.runnerName.active,
          label: "Last name",
          multiple: false,
          name: StoreOrderFilterKeys.runnerLastName,
          type: FieldType.text,
          value: opts.runnerName.value.lastName
        }
      ]),
      active: opts.runnerName.active
    },
    {
      ...createTextField(
        StoreOrderFilterKeys.customer,
        intl.formatMessage(messages.customer),
        opts.customer.value
      ),
      active: opts.customer.active
    },
    {
      ...createDateField(
        StoreOrderFilterKeys.created,
        intl.formatMessage(messages.placed),
        opts.created.value
      ),
      active: opts.created.active
    },
    {
      ...createDateField(
        StoreOrderFilterKeys.shipping,
        intl.formatMessage(messages.shipping),
        opts.shipping.value
      ),
      active: opts.shipping.active
    },
    {
      ...createOptionsField(
        StoreOrderFilterKeys.deliveryType,
        intl.formatMessage(messages.deliveryType),
        opts.deliveryType.value,
        true,
        [
          {
            label: intl.formatMessage(messages.express),
            value: DeliveryTypeEnum.EXPRESS
          },
          {
            label: intl.formatMessage(messages.expressLongDistance),
            value: DeliveryTypeEnum.EXPRESS_LONG_DISTANCE
          },
          {
            label: intl.formatMessage(messages.nextDay),
            value: DeliveryTypeEnum.NEXT_DAY
          },
          {
            label: intl.formatMessage(messages.inStore),
            value: DeliveryTypeEnum.IN_STORE_PICKUP
          },
          {
            label: intl.formatMessage(messages.sameDay),
            value: DeliveryTypeEnum.SAME_DAY
          },
          {
            label: intl.formatMessage(messages.nationwide),
            value: DeliveryTypeEnum.NATIONWIDE_DELIVERY
          }
        ]
      ),
      active: opts.deliveryType.active
    },
    {
      ...createOptionsField(
        StoreOrderFilterKeys.verticalType,
        intl.formatMessage(messages.verticalType),
        opts.verticalType.value,
        true,
        [
          {
            label: intl.formatMessage(messages.foodAndBeverages),
            value: StoreTypeEnum.FOOD_AND_BEVERAGES
          },
          {
            label: intl.formatMessage(messages.generalMerchandise),
            value: StoreTypeEnum.GENERAL_MERCHANDISE
          }
        ]
      ),
      active: opts.verticalType.active
    },
    {
      ...createTextField(
        StoreOrderFilterKeys.store,
        intl.formatMessage(messages.store),
        opts.store.value
      ),
      active: opts.store.active
    },
    {
      ...createOptionsField(
        StoreOrderFilterKeys.paymentType,
        intl.formatMessage(messages.paymentType),
        [opts.paymentType.value],
        false,
        [
          {
            label: intl.formatMessage(messages.cash),
            value: PaymentType.CASH
          },
          {
            label: intl.formatMessage(messages.card),
            value: PaymentType.CARD
          },
          {
            label: intl.formatMessage(messages.gcash),
            value: PaymentType.GCASH
          },
          {
            label: intl.formatMessage(messages.onlineBankingBPI),
            value: PaymentType.ONLINE_BANKING_BPI
          },
          {
            label: intl.formatMessage(messages.onlineBankingChinaBank),
            value: PaymentType.ONLINE_BANKING_CHINABANK
          },
          {
            label: intl.formatMessage(messages.onlineBankingRcbc),
            value: PaymentType.ONLINE_BANKING_RCBC
          },
          {
            label: intl.formatMessage(messages.onlineBankingUbp),
            value: PaymentType.ONLINE_BANKING_UBP
          },
          {
            label: intl.formatMessage(messages.eWalletGrabPay),
            value: PaymentType.EWALLET_GRABPAY
          },
          {
            label: intl.formatMessage(messages.eWalletMaya),
            value: PaymentType.EWALLET_PAYMAYA
          }
        ]
      ),
      active: opts.paymentType.active
    },
    {
      ...createAutocompleteField(
        StoreOrderFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts?.mall?.value,
        opts?.mall?.displayValues,
        true,
        opts?.mall?.choices,
        {
          hasMore: opts?.mall?.hasMore,
          initialSearch: "",
          loading: opts?.mall?.loading,
          onFetchMore: opts?.mall?.onFetchMore,
          onSearchChange: opts?.mall?.onSearchChange
        }
      ),
      active: opts?.mall?.active
    }
  ];

  return toExcludeOptions && toExcludeOptions.length > 0
    ? filterStructure.filter(
        filterItem => !toExcludeOptions.includes(filterItem.name)
      )
    : filterStructure;
}
